import { useWorkspaces } from "@/features/Workspaces/hooks/useWorkspaces";
import {
  Box,
  Button,
  Divider,
  Flex,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  Spacer,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Icon, MenuItem } from "@pufflig/prompt-studio-ui";
import { useEffect, useState } from "react";
import { ImportTemplateComponentProps } from "./types";

export const ImportTemplateFormSkeleton = () => {
  return (
    <Flex padding="spacing-lg" direction={["column", "column", "row"]}>
      <Stack>
        <Skeleton height="24rem" width="24rem" />
        <Skeleton height="2rem" width="12rem" />
        <Skeleton height="1rem" width="100%" />
      </Stack>
      <Box flex="1" mx="spacing-lg">
        <Divider orientation="vertical" bg="border.secondary" />
      </Box>
      <Stack minWidth="18rem" gap="spacing-lg">
        <Stack>
          <Skeleton height="1rem" width="12rem" />
          <Skeleton height="2rem" width="100%" />
        </Stack>
        <Stack>
          <Skeleton height="1rem" width="12rem" />
          <Skeleton height="2rem" width="100%" />
        </Stack>
        <Spacer />
        <Flex justifyContent="flex-end">
          <Button isDisabled variant="primary">
            Create Promptbook
          </Button>
        </Flex>
      </Stack>
    </Flex>
  );
};

export const ImportTemplateForm = (props: ImportTemplateComponentProps) => {
  const { templateName, templateDescription, templateImageUrl, teams, onCreatePromptbook } = props;
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

  const workspacesQuery = useWorkspaces(selectedTeam || undefined);

  const workspaces = workspacesQuery.data?.map((workspace) => ({
    workspaceName: workspace.name,
    workspaceId: workspace.workspace_id,
  }));

  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState("Untitled Promptbook");
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(workspaces?.[0].workspaceId || null);

  const selectedTeamName = teams?.find((team) => team.teamId === selectedTeam)?.teamName;
  const selectedWorkspaceName = workspaces?.find(
    (workspace) => workspace.workspaceId === selectedWorkspace,
  )?.workspaceName;

  const canCreatePromptbook = !!selectedWorkspace;

  useEffect(() => {
    if (workspaces && !selectedWorkspace) {
      setSelectedWorkspace(workspaces?.[0].workspaceId);
    }
  }, [workspaces]);

  return (
    <Flex padding="spacing-lg" direction={["column", "column", "row"]}>
      <Stack gap="spacing-lg">
        <Box>
          <Image
            bg="background.secondary"
            alt-text={templateName}
            objectFit="cover"
            height="24rem"
            width="24rem"
            borderRadius="radius-lg"
            src={templateImageUrl || ""}
          />
        </Box>
        <Stack gap="spacing-xxs">
          <Text textStyle="label/L/medium">{templateName}</Text>
          <Text textStyle="label/S/regular" color="foreground.secondary">
            {templateDescription}
          </Text>
        </Stack>
      </Stack>
      <Box flex="1" mx="spacing-lg">
        <Divider orientation="vertical" bg="border.secondary" />
      </Box>
      <Stack minWidth="18rem" gap="spacing-lg">
        <Stack>
          <Text textStyle="label/M/medium" color="foreground.secondary">
            Name your Promptbook
          </Text>
          <Input
            placeholder="Untitled Promptbook"
            value={name}
            onBlur={(e) => {
              if (!e.target.value) {
                setName("Untitled Promptbook");
              }
            }}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Stack>
        {teams?.length ? (
          <Stack>
            <Text textStyle="label/M/medium" color="foreground.secondary">
              Choose a team
            </Text>
            <Menu>
              <MenuButton textAlign="left" fontWeight={"normal"} as={Button} rightIcon={<Icon name="arrow-down-1" />}>
                {selectedTeamName || "Select..."}
              </MenuButton>
              <MenuList>
                {teams?.map((team) => (
                  <MenuItem
                    key={team.teamId}
                    primaryLabel={team.teamName}
                    rightIcon={selectedTeam === team.teamId ? <Icon name="check" /> : null}
                    onClick={() => {
                      if (selectedTeam === team.teamId) {
                        setSelectedTeam(null);
                        return;
                      }
                      setSelectedTeam(team.teamId);
                    }}
                  />
                ))}
              </MenuList>
            </Menu>
          </Stack>
        ) : null}
        <Stack>
          <Text textStyle="label/M/medium" color="foreground.secondary">
            Choose workspace
          </Text>
          <Menu>
            <MenuButton textAlign="left" fontWeight={"normal"} as={Button} rightIcon={<Icon name="arrow-down-1" />}>
              {selectedWorkspaceName || "Select..."}
            </MenuButton>
            <MenuList>
              {workspaces?.map((workspace) => (
                <MenuItem
                  key={workspace.workspaceId}
                  primaryLabel={workspace.workspaceName}
                  leftIcon={<Icon name="folder-2" />}
                  rightIcon={selectedWorkspace === workspace.workspaceId ? <Icon name="check" /> : null}
                  onClick={() => {
                    if (selectedWorkspace === workspace.workspaceId) {
                      setSelectedWorkspace(null);
                      return;
                    }
                    setSelectedWorkspace(workspace.workspaceId);
                  }}
                />
              ))}
            </MenuList>
          </Menu>
        </Stack>
        <Spacer />
        <Flex justifyContent="flex-end">
          <Tooltip isDisabled={canCreatePromptbook} label="You must select a team and workspace to create a promptbook">
            <Button
              isLoading={isCreating}
              isDisabled={!canCreatePromptbook}
              variant="primary"
              onClick={async () => {
                setIsCreating(true);
                if (!selectedWorkspace) return;
                await onCreatePromptbook(selectedWorkspace, name, selectedTeam || undefined);
                setIsCreating(false);
              }}
            >
              Create Promptbook
            </Button>
          </Tooltip>
        </Flex>
      </Stack>
    </Flex>
  );
};
